import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  checkData,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SnCRetailSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  /** Full dashboard payload — includes SnC Outlet Setup for dropdown options */
  fieldValues: any;
}) => {
  const [outletName, setOutletName] = useState<string | null>(null);
  const [itemNumber, setItemNumber] = useState<string | null>(null);
  const [itemDescription, setItemDescription] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [outletErr, setOutletErr] = useState("");
  const [itemNumErr, setItemNumErr] = useState("");
  const [descErr, setDescErr] = useState("");
  const [outletList, setOutletList] = useState<string[]>([]);

  useEffect(() => {
    if (!fieldValues) {
      return;
    }
    const retail = fieldValues[SnCPropertyEnum.retailSetup];
    setItems(Array.isArray(retail) ? retail : []);
    setOutletList(
      fillAutocompleteValues(
        fieldValues[SnCPropertyEnum.outletSetup] ?? [],
        DNDFieldsEnum.OUTLETNAME,
        "",
      ) ?? [],
    );
  }, [fieldValues]);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    "Outlet Name": outletName,
    "Item Number": itemNumber,
    "Item Description": itemDescription,
  };

  const validateInput = (): boolean => {
    setOutletErr("");
    setItemNumErr("");
    setDescErr("");
    if (!outletName) {
      setOutletErr("Outlet Name" + validationMessages.sncMandatorySuffix);
    }
    if (!itemNumber) {
      setItemNumErr("Item Number" + validationMessages.sncMandatorySuffix);
    }
    if (!itemDescription) {
      setDescErr("Item Description" + validationMessages.sncMandatorySuffix);
    }
    if (!outletName || !itemNumber || !itemDescription) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        itemNumber,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setItemNumErr(validationMessages.originCode);
      return false;
    }
    if (
      !validateString(
        itemDescription,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setDescErr(validationMessages.originName);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (
      otherItems?.some(
        (item: any) =>
          item["Outlet Name"] === outletName &&
          item["Item Number"] === itemNumber,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.retailSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (
      (items ?? []).some(
        (item: any) =>
          item["Outlet Name"] === outletName &&
          item["Item Number"] === itemNumber,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setOutletErr("");
      setItemNumErr("");
      setDescErr("");
      setOutletName(list["Outlet Name"] ?? null);
      setItemNumber(list["Item Number"] ?? null);
      setItemDescription(list["Item Description"] ?? null);
    } else {
      setOutletName(null);
      setItemNumber(null);
      setItemDescription(null);
      setOutletErr("");
      setItemNumErr("");
      setDescErr("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Retail Setup
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Retail Setup",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel>
              Outlet Name *
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <Autocomplete
              size="small"
              options={removeSampleTestArray(outletList)}
              value={outletName}
              disabled={!outletList?.length}
              onChange={(_, newValue) => {
                setOutletName(newValue);
                setOutletErr("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  placeholder="Select outlet"
                  error={!!outletErr}
                  helperText={
                    outletErr ||
                    (!outletList?.length
                      ? "Add outlets in Outlet Setup first"
                      : "")
                  }
                />
              )}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Item Number*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              type="number"
              variant="outlined"
              size="small"
              value={itemNumber ?? ""}
              error={!!itemNumErr}
              helperText={itemNumErr}
              onChange={(e) => {
                setItemNumber(e.target.value || null);
                setItemNumErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Item Description*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={itemDescription ?? ""}
              error={!!descErr}
              helperText={descErr}
              onChange={(e) => {
                setItemDescription(e.target.value || null);
                setDescErr("");
              }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Retail Setup
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Retail Setup Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Outlet Name"
              name="Item Number"
            />
          ) : (
            <Typography variant="body2">No Retail Setup Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCRetailSetup;
